import React, { useMemo, useState } from 'react';
import type { LscScheduleBindingCandidateMatch, LscVodCandidate } from '../types';
import { assignVodMatch, LscApiError } from '../api';
import ErrorBanner from './ErrorBanner';
import { displaySeasonLabel } from '../seasonDisplay';
import {
  formatDetectedVodMatchup,
  formatScheduleCandidateMatch,
  formatScheduleMatchup,
  truthScheduleMatchRankLabel,
  truthScheduleMatchWarning,
} from '../vodTruth';

interface ScheduleMatchSelectorProps {
  leagueId: string;
  candidate: LscVodCandidate;
  matches: LscScheduleBindingCandidateMatch[];
  fallbackMatches?: LscScheduleBindingCandidateMatch[];
  boundMatchId?: string | null;
  onAssigned: () => Promise<void>;
}

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const ScheduleMatchSelector: React.FC<ScheduleMatchSelectorProps> = ({
  leagueId,
  candidate,
  matches,
  fallbackMatches = [],
  boundMatchId,
  onAssigned,
}) => {
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const [selectedFallbackId, setSelectedFallbackId] = useState<string | null>(null);

  const detectedMatchup = formatDetectedVodMatchup(candidate);
  const assignedMatchId = candidate.competition_match_id ?? null;

  const visibleMatches = useMemo(() => {
    const primaryIds = new Set(matches.map((match) => match.match_id));
    const assignedFallback =
      assignedMatchId && !primaryIds.has(assignedMatchId)
        ? fallbackMatches.find((match) => match.match_id === assignedMatchId)
        : null;
    if (assignedFallback) {
      return [...matches, assignedFallback];
    }
    return matches;
  }, [assignedMatchId, fallbackMatches, matches]);

  const runAssign = async (matchId: string) => {
    setAssigningId(matchId);
    setError(null);
    try {
      await assignVodMatch(leagueId, candidate.id, {
        competition_match_id: matchId,
      });
      if (fallbackMatches.some((match) => match.match_id === matchId)) {
        setSelectedFallbackId(matchId);
      }
      await onAssigned();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not assign match.');
    } finally {
      setAssigningId(null);
    }
  };

  if (!matches.length && !fallbackMatches.length) {
    return null;
  }

  const singleBound = matches.length === 1 && boundMatchId === matches[0].match_id;

  const renderMatchRow = (match: LscScheduleBindingCandidateMatch) => {
    const isAssigned = assignedMatchId === match.match_id;
    const rankLabel = truthScheduleMatchRankLabel(match);
    const warning =
      selectedFallbackId === match.match_id || (isAssigned && match.is_fallback)
        ? truthScheduleMatchWarning(match)
        : null;

    return (
      <li
        key={match.match_id}
        className={`rounded-lg border p-3 text-sm ${
          isAssigned ? 'border-green-700 bg-green-900/20' : 'border-secondary-700'
        }`}
        data-testid={`schedule-match-option-${match.match_id}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <dl className="grid gap-1 sm:grid-cols-2 flex-1">
            <div className="sm:col-span-2">
              <dt className="text-secondary-500">Schedule match</dt>
              <dd className="text-white font-medium" data-testid="schedule-match-timeline">
                {formatScheduleCandidateMatch(match)}
              </dd>
            </div>
            {rankLabel && (
              <div className="sm:col-span-2">
                <p
                  className="text-xs text-amber-200/90"
                  data-testid={`schedule-match-rank-label-${match.match_id}`}
                >
                  {rankLabel}
                </p>
              </div>
            )}
            {warning && (
              <div className="sm:col-span-2">
                <p
                  className="text-xs text-amber-200/90"
                  data-testid={`schedule-match-warning-${match.match_id}`}
                >
                  {warning}
                </p>
              </div>
            )}
            <div>
              <dt className="text-secondary-500">Season</dt>
              <dd className="text-secondary-200">
                {match.edition
                  ? `${displaySeasonLabel(match.season_label, null, match.edition) || '—'} · ${
                      /^madden-(\d{2})$/i.test(String(match.edition))
                        ? `Madden ${String(match.edition).split('-')[1]}`
                        : match.edition
                    }`
                  : (displaySeasonLabel(match.season_label, null, match.edition) || '—')}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Week / stage</dt>
              <dd className="text-secondary-200">{match.stage_label ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Away @ Home</dt>
              <dd className="text-secondary-200" data-testid="schedule-match-matchup">
                {formatScheduleMatchup(match)}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Scheduled</dt>
              <dd className="text-secondary-200">{formatWhen(match.scheduled_at)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-secondary-500">Archive / link</dt>
              <dd className="text-secondary-200">
                {match.vod_linked ? 'VOD linked' : 'No VOD linked'}
                {isAssigned && (
                  <span
                    className="ml-2 inline-flex rounded-full bg-green-900/50 border border-green-700 px-2 py-0.5 text-xs text-green-200"
                    data-testid={`schedule-match-assigned-badge-${match.match_id}`}
                  >
                    Assigned
                  </span>
                )}
              </dd>
            </div>
          </dl>
          <button
            type="button"
            className="btn-secondary shrink-0"
            disabled={Boolean(assigningId) || isAssigned}
            onClick={() => runAssign(match.match_id)}
            data-testid={`schedule-match-assign-${match.match_id}`}
          >
            {assigningId === match.match_id
              ? 'Assigning...'
              : isAssigned
                ? 'Assigned'
                : 'Select'}
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-3" data-testid="schedule-match-selector">
      {detectedMatchup && (
        <p className="text-sm text-secondary-300" data-testid="schedule-detected-vod-matchup">
          Detected from VOD: {detectedMatchup}
        </p>
      )}

      {!singleBound && (
        <p className="text-sm text-secondary-400" data-testid="schedule-select-one-match">
          Select one schedule match
        </p>
      )}

      {assignedMatchId && !singleBound && (
        <p className="text-xs text-secondary-500" data-testid="schedule-replace-assignment-note">
          Selecting another match replaces the current assignment.
        </p>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {singleBound ? (
        <button
          type="button"
          className="btn-primary"
          disabled={Boolean(assigningId) || assignedMatchId === boundMatchId}
          onClick={() => runAssign(boundMatchId!)}
          data-testid="schedule-match-assign-bound"
        >
          {assigningId
            ? 'Assigning...'
            : assignedMatchId === boundMatchId
              ? 'Assigned'
              : 'Select'}
        </button>
      ) : (
        <>
          <ul className="space-y-2" data-testid="schedule-primary-matches">
            {visibleMatches.map(renderMatchRow)}
          </ul>
          {fallbackMatches.length > 0 && (
            <div className="space-y-2" data-testid="schedule-fallback-section">
              <button
                type="button"
                className="text-sm text-secondary-400 hover:text-secondary-200"
                onClick={() => setShowFallback((open) => !open)}
                data-testid="schedule-fallback-toggle"
              >
                {showFallback
                  ? 'Hide lower-confidence fallback matches'
                  : 'Show lower-confidence fallback matches'}
              </button>
              {showFallback && (
                <ul className="space-y-2" data-testid="schedule-fallback-matches">
                  {fallbackMatches.map(renderMatchRow)}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScheduleMatchSelector;
